import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaEye,
  FaQrcode,
} from 'react-icons/fa';
import { Wrench, CheckCircle2, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import EquipmentForm from './EquipmentForm';
import EquipmentDetailModal from './EquipmentDetailModal';
import '../styles/InventoryEquipment.css';

const FIND = `
  query FindAllEquipment {
    findAllEquipment {
      id
      assetNumber
      equipmentType
      brandModel
      brand
      model
      serialNumber
      purchaseDate
      purchaseValue
      assignedTo
      currentLocation
      currentSite
      lastServiceDate
      nextServiceDate
      serviceFrequency
      status
      notes
    }
  }
`;

const REMOVE = `
  mutation RemoveEquipment($id: String!) {
    removeEquipment(id: $id)
  }
`;

const TABS = [
  'All',
  'Vacuums',
  'Scrubbers',
  'Extractors',
  'Pressure Washers',
  'Other',
];

const EQUIPMENT_VIEW_KEYS = [{ param: 'asset', rowKey: 'assetNumber' }];
const EQUIPMENT_CLEAR_KEYS = ['asset'];

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

function statusLabel(status) {
  const s = String(status || '');
  if (s === 'InUse') return 'In Use';
  if (s === 'InService') return 'In Service';
  if (s === 'ServiceOverdue') return 'Service Overdue';
  return s || '—';
}

function statusClass(status) {
  const s = String(status || '');
  if (s === 'InUse') return 'ieq-pill--inuse';
  if (s === 'InService') return 'ieq-pill--inservice';
  if (s === 'ServiceOverdue') return 'ieq-pill--overdue';
  return 'ieq-pill--active';
}

function matchesTab(row, tab) {
  if (tab === 'All') return true;
  const t = String(row.equipmentType || '').toLowerCase();
  if (tab === 'Vacuums') return t.includes('vacuum');
  if (tab === 'Scrubbers') return t.includes('scrubber');
  if (tab === 'Extractors') return t.includes('extractor');
  if (tab === 'Pressure Washers') return t.includes('pressure') || t.includes('washer');
  if (tab === 'Other') {
    return !(
      t.includes('vacuum') ||
      t.includes('scrubber') ||
      t.includes('extractor') ||
      t.includes('pressure') ||
      t.includes('washer')
    );
  }
  return true;
}

function isDueForService(row) {
  if (String(row.status) === 'ServiceOverdue') return true;
  if (!row.nextServiceDate) return false;
  const d = new Date(row.nextServiceDate);
  if (Number.isNaN(d.getTime())) return false;
  const soon = new Date();
  soon.setDate(soon.getDate() + 14);
  return d <= soon;
}

export default function EquipmentDashboard() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const overlay = useAdminListOverlay(rows, {
    allowedTabs: TABS,
    extraViewKeys: EQUIPMENT_VIEW_KEYS,
    extraClearKeys: EQUIPMENT_CLEAR_KEYS,
  });
  const tab = overlay.tab;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await graphqlRequest(FIND);
      setRows(
        Array.isArray(data?.findAllEquipment) ? data.findAllEquipment : [],
      );
    } catch (e) {
      toast.error(e?.message || 'Failed to load equipment');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const stats = useMemo(() => {
    const total = rows.length;
    const inUse = rows.filter((r) => r.status === 'InUse').length;
    const inService = rows.filter((r) => r.status === 'InService').length;
    const due = rows.filter((r) => isDueForService(r)).length;
    return { total, inUse, inService, due };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!matchesTab(r, tab)) return false;
      if (!q) return true;
      return `${r.assetNumber} ${r.equipmentType} ${r.brandModel} ${r.assignedTo} ${r.currentSite}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search, tab]);

  const nextAssetNumber = useMemo(() => {
    let max = 0;
    for (const row of rows) {
      const m = String(row.assetNumber || '').match(/(\d+)$/);
      if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
    }
    return `ASS-${String(max + 1).padStart(3, '0')}`;
  }, [rows]);

  const handleDelete = async (row) => {
    if (!await appConfirmMessage(`Delete equipment "${row.assetNumber}"?`)) return false;
    try {
      await graphqlRequest(REMOVE, { id: row.id });
      toast.success('Equipment deleted');
      await load();
      return true;
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
      return false;
    }
  };

  return (
    <div className="ieq-page">
      <header className="ieq-header">
        <h1>Equipment</h1>
        <button
          type="button"
          className="ieq-btn ieq-btn--primary"
          onClick={() => overlay.openAdd()}
        >
          <FaPlus /> Add Equipment
        </button>
      </header>

      <section className="ieq-stats">
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--purple" aria-hidden>
            <Wrench size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">TOTAL ASSETS</div>
            <div className="ieq-stat-value">{stats.total}</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--green" aria-hidden>
            <CheckCircle2 size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">IN USE</div>
            <div className="ieq-stat-value">{stats.inUse}</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--orange" aria-hidden>
            <RefreshCw size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">IN SERVICE</div>
            <div className="ieq-stat-value">{stats.inService}</div>
          </div>
        </article>
        <article className="ieq-stat">
          <div className="ieq-stat-icon ieq-stat-icon--orange" aria-hidden>
            <AlertTriangle size={20} strokeWidth={2.25} />
          </div>
          <div>
            <div className="ieq-stat-label">DUE FOR SERVICE</div>
            <div className="ieq-stat-value">{stats.due}</div>
          </div>
        </article>
      </section>

      <div className="ieq-panel">
        <div className="ieq-tabs-row">
          <div className="ieq-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                className={`ieq-tab${tab === t ? ' is-active' : ''}`}
                onClick={() => overlay.setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <label className="ieq-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assets..."
            />
          </label>
        </div>

        <div className="ieq-table-wrap">
          <table className="ieq-table">
            <thead>
              <tr>
                <th>Asset #</th>
                <th>Type</th>
                <th>Brand / Model</th>
                <th>Purchase Date</th>
                <th>Value</th>
                <th>Assigned To</th>
                <th>Site</th>
                <th>Last Service</th>
                <th>Next Service</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="ieq-empty">
                    Loading equipment…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="11" className="ieq-empty">
                    No equipment found. Click Add Equipment to create one.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="ieq-id"
                        onClick={() => overlay.openView(row)}
                      >
                        {row.assetNumber}
                      </button>
                    </td>
                    <td>{row.equipmentType || '—'}</td>
                    <td>{row.brandModel || '—'}</td>
                    <td>{formatDate(row.purchaseDate)}</td>
                    <td>{formatMoney(row.purchaseValue)}</td>
                    <td>{row.assignedTo || '—'}</td>
                    <td>{row.currentSite || '—'}</td>
                    <td>{formatDate(row.lastServiceDate)}</td>
                    <td>{formatDate(row.nextServiceDate)}</td>
                    <td>
                      <span className={`ieq-pill ${statusClass(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                    </td>
                    <td>
                      <div className="ieq-actions">
                        <button
                          type="button"
                          className="ieq-icon-btn"
                          title="View"
                          onClick={() => overlay.openView(row)}
                        >
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="ieq-icon-btn"
                          title="Edit"
                          onClick={() => overlay.openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="ieq-icon-btn"
                          title="QR code — print and stick on this asset"
                          onClick={() => overlay.openView(row)}
                        >
                          <FaQrcode />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="ieq-foot">
          Showing {filtered.length} asset{filtered.length === 1 ? '' : 's'}
        </div>
      </div>

      {overlay.showForm && (
        <EquipmentForm
          editData={overlay.editing}
          nextAssetNumber={nextAssetNumber}
          onClose={overlay.closeOverlay}
          onSaved={load}
          onDelete={async (row) => {
            const ok = await handleDelete(row);
            if (ok) overlay.closeOverlay();
          }}
        />
      )}

      {overlay.viewing && (
        <EquipmentDetailModal
          equipment={overlay.viewing}
          onClose={overlay.closeOverlay}
          onEdit={overlay.openEdit}
        />
      )}
    </div>
  );
}
